import { useCallback, useEffect, useRef } from "react";
import { MIC_TOGGLE_TARGET, resolveMicToggleTarget } from "../utils/micStartTarget";

/**
 * Routes a single active mic session between sidebar dictation and the report editor
 * based on which input surface has focus. Existing mic hooks keep their own behavior;
 * this only coordinates stop/start on focus change and unified toggle.
 *
 * Idle mic starts always target Findings (see resolveMicToggleTarget / handleSidebarMicClick).
 * Focus handlers never start recording — they only run when a session is already active.
 */
export function useMicFocusOrchestrator({
  isEditorMicActive,
  isSidebarRecording,
  stopEditorMic,
  startEditorMic,
  stopSidebarMic,
  startSidebarMic,
}) {
  const switchingRef = useRef(false);
  const pendingSwitchRef = useRef(null);
  const editorActiveRef = useRef(isEditorMicActive);
  const sidebarActiveRef = useRef(isSidebarRecording);

  useEffect(() => {
    editorActiveRef.current = isEditorMicActive;
  }, [isEditorMicActive]);

  useEffect(() => {
    sidebarActiveRef.current = isSidebarRecording;
  }, [isSidebarRecording]);

  const micToggleTarget = resolveMicToggleTarget({
    isEditorMicActive,
    isSidebarRecording,
  });

  const isAnyMicActive = isEditorMicActive || isSidebarRecording;

  const activateSidebarMic = useCallback(async () => {
    await Promise.resolve(stopEditorMic?.());
    editorActiveRef.current = false;
    if (!sidebarActiveRef.current) {
      await startSidebarMic?.();
      sidebarActiveRef.current = true;
    }
  }, [stopEditorMic, startSidebarMic]);

  const activateEditorMic = useCallback(async () => {
    await stopSidebarMic?.();
    sidebarActiveRef.current = false;
    if (!editorActiveRef.current) {
      await startEditorMic?.();
      editorActiveRef.current = true;
    }
  }, [startEditorMic, stopSidebarMic]);

  const flushPendingSwitch = useCallback(async () => {
    if (switchingRef.current) return;

    const target = pendingSwitchRef.current;
    if (!target) return;

    if (!editorActiveRef.current && !sidebarActiveRef.current) {
      pendingSwitchRef.current = null;
      return;
    }

    switchingRef.current = true;
    pendingSwitchRef.current = null;

    try {
      if (target === MIC_TOGGLE_TARGET.FINDINGS) {
        if (sidebarActiveRef.current && !editorActiveRef.current) return;
        await activateSidebarMic();
      } else {
        if (editorActiveRef.current && !sidebarActiveRef.current) return;
        await activateEditorMic();
      }
    } finally {
      switchingRef.current = false;
      if (pendingSwitchRef.current) {
        void flushPendingSwitch();
      }
    }
  }, [activateSidebarMic, activateEditorMic]);

  const queueFocusSwitch = useCallback(
    (target) => {
      pendingSwitchRef.current = target;
      void flushPendingSwitch();
    },
    [flushPendingSwitch],
  );

  const handleSidebarFocus = useCallback(() => {
    if (!editorActiveRef.current && !sidebarActiveRef.current) return;
    if (sidebarActiveRef.current && !editorActiveRef.current) return;
    queueFocusSwitch(MIC_TOGGLE_TARGET.FINDINGS);
  }, [queueFocusSwitch]);

  const handleEditorFocus = useCallback(() => {
    if (!editorActiveRef.current && !sidebarActiveRef.current) return;
    if (editorActiveRef.current && !sidebarActiveRef.current) return;
    queueFocusSwitch(MIC_TOGGLE_TARGET.REPORT);
  }, [queueFocusSwitch]);

  return {
    handleSidebarFocus,
    handleEditorFocus,
    micToggleTarget,
    isAnyMicActive,
  };
}
